import React, { useState, useEffect, useRef, useCallback } from 'react';
import Loading from '../../../Elements/loading';
import AuthService from '../../../Services/authService';
import UserService from '../../../Services/userService';
import UserItem from './UserItem';
import ErrorMessage from '../../../Elements/ErrorMessage';
import CookieHelper from '../../../Helpers/cookieHelper';
import { iconsEnabled, ICONS_COOKIE } from '../../../Helpers/iconHelper';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import DataWithInPlaceEdit from '../../../Elements/DataWithInPlaceEdit';
import FaIcon from '../../../Elements/FaIcon';
import stringHelper from '../../../Helpers/stringHelper';
import { HOLIDAY_LOADERS } from '../../../Elements/HolidayLoaders/holidayLoaderRegistry';
import AvailabilityPlannerService from '../../../Services/AvailabilityPlannerService';
import { LOCATION } from '../../TimeSheet/AvailabilityPlanner/availabilityTaxonomy';

// ---- helpers ----
function initialsOf(first, last, fallback) {
  const a = (first || '').trim();
  const b = (last || '').trim();
  const ini = (a ? a[0] : '') + (b ? b[0] : '');
  if (ini) return ini.toUpperCase();
  const f = (fallback || '').trim();
  return f ? f.slice(0, 2).toUpperCase() : '?';
}

// roles may arrive as an array or a comma-separated string
function toRoleArray(roles) {
  if (Array.isArray(roles)) return roles.filter(Boolean);
  if (typeof roles === 'string')
    return roles
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  return [];
}

function tierLabel(tp) {
  const s = String(tp == null ? '' : tp).trim();
  if (!s) return '';
  return /tier/i.test(s) ? s : 'Tier ' + s;
}

// ---- presentational pieces ----
function EqMark() {
  return (
    <span className='eq-mark'>
      <span />
      <span />
    </span>
  );
}

function PField({ label, value, mono, span2, muted, children }) {
  return (
    <div className={'pfield' + (span2 ? ' span2' : '')}>
      <span className='pf-k'>{label}</span>
      {children ? children : <span className={'pf-v' + (mono ? ' mono' : '') + (muted ? ' muted' : '')}>{value}</span>}
    </div>
  );
}

function ProfSection({ eyebrow, title, children }) {
  return (
    <section className='prof-section'>
      <div className='prof-sec-head'>
        <EqMark />
        <div>
          <div className='eyebrow'>{eyebrow}</div>
          <h2>{title}</h2>
        </div>
      </div>
      {children}
    </section>
  );
}

function ProfActive({ on }) {
  return (
    <span className={'pf-active' + (on ? '' : ' no')}>
      <span className='ck'>
        <FaIcon iconName={on ? 'fa-solid fa-check' : 'fa-solid fa-xmark'} />
      </span>
      {on ? 'Active' : 'Inactive'}
    </span>
  );
}

function RoleChips({ roles, alt }) {
  return (
    <div className='role-chips'>
      {toRoleArray(roles).map((r) => (
        <span
          className={'role-chip' + (alt ? ' alt' : '')}
          key={r}
        >
          {r}
        </span>
      ))}
    </div>
  );
}

function OfficeCard({ user, index }) {
  const office = user.officeName || '';
  return (
    <div className='office-card'>
      <div className='office-head'>
        <span className='oh-ico'>
          <FaIcon iconName='fa-solid fa-user' />
        </span>
        <h3>
          {stringHelper.ordinalNumber(index + 1)} User — {office.replace('BARE ', '')}
        </h3>
        <span className='hero-chip flag'>{office}</span>
      </div>
      <div className='office-body'>
        <div className='office-meta'>
          <PField
            label='UserId'
            value={user.userId}
            mono
          />
          <PField
            label='UserName'
            value={user.userName}
            mono
          />
          <PField
            label='Office'
            value={office}
          />
        </div>
        <div className='office-roles'>
          <div className='or-lbl'>Caspian Roles · {toRoleArray(user.roles).length}</div>
          <RoleChips
            roles={user.roles}
            alt
          />
        </div>
      </div>
    </div>
  );
}

// ---- page ----
export default function Profile() {
  const authServiceRef = useRef(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [mainUser, setMainUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [icons, setIcons] = useState(iconsEnabled());
  const [videos, setVideos] = useState(CookieHelper.getCookie('videos', 'true') === 'true');
  const [loaderPref, setLoaderPref] = useState(CookieHelper.getCookie('loaderPref', 'auto'));
  const [defaultWorkLocation, setDefaultWorkLocation] = useState('');

  const populate = useCallback(() => {
    const svc = authServiceRef.current;
    const lu = svc.GetLoggedInUser();
    setLoggedInUser(lu);
    setLoading(true);
    UserService.GetUserById(lu.userId)
      .then((result) => {
        setMainUser(new UserItem(result));
        setLoading(false);
      })
      .catch(() => ErrorMessage.SetErrorMessage('P86'));
  }, []);

  useEffect(() => {
    const svc = AuthService.getInstance();
    authServiceRef.current = svc;
    const handler = () => populate();
    svc.listenSwitchUserEvent(handler);
    populate();
    AvailabilityPlannerService.GetDefaultWorkLocation()
      .then((location) => setDefaultWorkLocation(location || 'office'))
      .catch(() => ErrorMessage.SetErrorMessage('P88'));
    return () => svc.unlistenSwitchUserEvent(handler);
  }, [populate]);

  // Self-saving (no save button): persists as soon as a location is picked.
  const changeDefaultWorkLocation = (e) => {
    const location = e.target.value;
    setDefaultWorkLocation(location);
    AvailabilityPlannerService.SetDefaultWorkLocation(location)
      .then((result) => {
        if (result && (result.error || result.Error)) {
          ErrorMessage.SetErrorMessage(result.error || result.Error);
        } else {
          ErrorMessage.SetNotification('Default work location saved.');
        }
      })
      .catch(() => ErrorMessage.SetErrorMessage('P87'));
  };

  const toggleCookie = (name, current, setter, onChanged) => () => {
    const next = !current;
    CookieHelper.setCookie(name, next.toString());
    setter(next);
    if (onChanged) onChanged();
  };

  const changeLoaderPref = (e) => {
    const value = e.target.value;
    CookieHelper.setCookie('loaderPref', value);
    setLoaderPref(value);
  };

  if (!loggedInUser) return null;

  const fullName = `${loggedInUser.firstName || ''} ${loggedInUser.lastName || ''}`.trim();

  return (
    <div className='contentwrapper bravo-ds'>
      <div className='profile-scroll'>
        <div className='profile-inner'>
          {/* hero */}
          <div className='prof-hero'>
            <span className='avatar'>
              {initialsOf(loggedInUser.firstName, loggedInUser.lastName, loggedInUser.userName)}
            </span>
            <div className='who'>
              <div className='eyebrow'>Bravo · BARE International</div>
              <h1>{fullName || loggedInUser.userName}</h1>
              <div className='sub'>
                {mainUser && mainUser.IsActive ? (
                  <span className='hero-chip'>
                    <span className='gdot' /> Active
                  </span>
                ) : null}
                <span className='hero-chip'>
                  UserId <b>{loggedInUser.userId}</b>
                </span>
                {mainUser && mainUser.OfficeName ? <span className='hero-chip'>{mainUser.OfficeName}</span> : null}
                {mainUser && mainUser.OfficialJobTitle ? (
                  <span className='hero-chip'>{mainUser.OfficialJobTitle}</span>
                ) : null}
                {mainUser && tierLabel(mainUser.TierPos) ? (
                  <span className='hero-chip'>{tierLabel(mainUser.TierPos)}</span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Bravo User */}
          <ProfSection
            eyebrow='Identity'
            title='Bravo User'
          >
            <div className='pfield-grid'>
              <PField
                label='First name'
                value={loggedInUser.firstName}
              />
              <PField
                label='Last name'
                value={loggedInUser.lastName}
              />
              <PField
                label='UserName'
                value={loggedInUser.userName}
                mono
              />
              <PField
                label='Email'
                value={loggedInUser.email}
              />
              <PField
                label='UserId'
                value={loggedInUser.userId}
                mono
              />
              {mainUser ? (
                <div className='pfield editable'>
                  <span className='pf-k'>Back-up</span>
                  <DataWithInPlaceEdit
                    dataToShow={mainUser.SubstitutedByName ? mainUser.SubstitutedByName : 'None'}
                    serviceFunction={(d) => UserService.UpdateSubstitutedBy(mainUser.UserId, d)}
                    type={InputTypesEnum.UserSelect}
                    value={mainUser.SubstitutedById}
                    clickOnSave={populate}
                  />
                </div>
              ) : null}
              {mainUser ? (
                <div className='pfield editable span2'>
                  <span className='pf-k'>Time Zone</span>
                  <DataWithInPlaceEdit
                    dataToShow={mainUser.TimeZoneOffsetName ? mainUser.TimeZoneOffsetName : 'None'}
                    serviceFunction={(d) => UserService.UpdateTimeZone(mainUser.UserId, d)}
                    type={InputTypesEnum.TimeZone}
                    value={mainUser.TimeZoneOffset}
                    clickOnSave={populate}
                  />
                </div>
              ) : null}
              <PField
                label='Bravo Roles'
                span2
              >
                <RoleChips roles={loggedInUser.roles} />
              </PField>
            </div>
          </ProfSection>

          {/* Settings */}
          <ProfSection
            eyebrow='Preferences'
            title='Settings'
          >
            <div className='settings-grid'>
              <PField label='Default work location'>
                <select
                  className='prof-select'
                  value={defaultWorkLocation}
                  onChange={changeDefaultWorkLocation}
                >
                  {Object.entries(LOCATION).map(([code, loc]) => (
                    <option
                      key={code}
                      value={code}
                    >
                      {loc.label}
                    </option>
                  ))}
                </select>
              </PField>
              <div className='pref-card'>
                <div className='pref-row'>
                  <span className='pr-txt'>Loading animation</span>
                  <select
                    className='prof-select'
                    style={{ maxWidth: 'min(380px, 60%)' }}
                    value={loaderPref}
                    onChange={changeLoaderPref}
                  >
                    <option value='auto'>Automatic (holidays)</option>
                    <option value='off'>None (plain mark)</option>
                    <option value='random'>Random (surprise me)</option>
                    <option value='unicorn'>Unicorn</option>
                    {[...new Set(HOLIDAY_LOADERS.map((h) => h.group))].map((group) => (
                      <optgroup
                        key={group}
                        label={group}
                      >
                        {HOLIDAY_LOADERS.filter((h) => h.group === group).map((h) => (
                          <option
                            key={h.id}
                            value={h.id}
                          >
                            {h.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className='pref-row'>
                  <span className='pr-txt'>I like icons</span>
                  <label className='switch'>
                    {/* Icon visibility is read at render time all over the app (sidebar, tab
                        strips, page title) — reload so the preference applies everywhere at once. */}
                    <input
                      type='checkbox'
                      checked={icons}
                      onChange={toggleCookie(ICONS_COOKIE, icons, setIcons, () => window.location.reload())}
                    />
                    <span className='track' />
                    <span className='thumb' />
                  </label>
                </div>
                <div className='pref-row'>
                  <span className='pr-txt'>I like videos</span>
                  <label className='switch'>
                    <input
                      type='checkbox'
                      checked={videos}
                      onChange={toggleCookie('videos', videos, setVideos)}
                    />
                    <span className='track' />
                    <span className='thumb' />
                  </label>
                </div>
              </div>
            </div>
          </ProfSection>

          {/* Caspian Primary User */}
          {loading ? (
            <Loading />
          ) : mainUser ? (
            <>
              <ProfSection
                eyebrow='Caspian'
                title='Caspian Primary User'
              >
                <div className='pfield-grid'>
                  <PField
                    label='UserName'
                    value={mainUser.UserName}
                    mono
                  />
                  <PField
                    label='Email'
                    value={mainUser.Email}
                  />
                  <PField
                    label='Office'
                    value={mainUser.OfficeName}
                  />
                  <PField
                    label='Department'
                    value={mainUser.Department}
                  />
                  <PField
                    label='OfficialJobTitle'
                    value={mainUser.OfficialJobTitle}
                  />
                  <PField
                    label='SuperVisorName'
                    value={mainUser.SuperVisorName}
                  />
                  <PField label='Is Active?'>
                    <ProfActive on={!!mainUser.IsActive} />
                  </PField>
                  <PField label='TierPos'>
                    <span className='tier-pill'>
                      <span className='dot' /> {tierLabel(mainUser.TierPos)}
                    </span>
                  </PField>
                  <PField
                    label='UserId'
                    value={mainUser.UserId}
                    mono
                  />
                  <PField
                    label='TimesheetUserUserName'
                    value={mainUser.TimesheetUserUserName}
                    mono
                  />
                </div>
              </ProfSection>

              <ProfSection
                eyebrow='Caspian'
                title='Caspian Office Users'
              >
                {(mainUser.RelatedUsers || []).map((u, i) => (
                  <OfficeCard
                    user={u}
                    index={i}
                    key={`ou-${i}`}
                  />
                ))}
              </ProfSection>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
